import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag, Repeat, Ticket, Star, ArrowRight, Sparkles, CircleDollarSign, Coins,
  User, Mail, Phone, GraduationCap, CalendarDays, ChevronDown, ClipboardCheck, Lock,
  FileText, Upload, X as XIcon, Loader2, CheckCircle2, Heart, Smile, Rocket, Compass, PlayCircle,
} from "lucide-react";
import { myApi } from "../api";
import { useMyAuth } from "../contexts/MyAuthContext";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);

const CARDS = [
  { key: "encomendas", label: "Encomendas", icon: ShoppingBag, href: "/my/encomendas", accent: "#0F1829" },
  { key: "subscricoes", label: "Subscrições", icon: Repeat, href: "/my/subscricoes", accent: "#6F7559" },
  { key: "vales", label: "Vales disponíveis", icon: Ticket, href: "/my/carteira", accent: "#6F4F1D" },
  { key: "creditos", label: "Créditos disponíveis", icon: Coins, href: "/my/comissoes", accent: "#978D78" },
  { key: "comissoes_a_receber", label: "Comissões a receber", icon: CircleDollarSign, href: "/my/comissoes", accent: "#4A5E2E", isMoney: true },
];

const HIGHLIGHTS = [
  {
    key: "coach",
    title: "Conhece-te melhor",
    href: "/my/coach",
    external: false,
    icon: Compass,
    gradient: "from-[#4A5E2E]/85 via-[#4A5E2E]/70 to-[#71874C]/85",
    bgImage: "https://images.unsplash.com/photo-1476611317561-60117649dd94?w=1200&q=75&auto=format&fit=crop",
    bgPos: "center 40%",
    cta: "Explorar",
  },
  {
    key: "dreams",
    title: "Trabalha os teus Sonhos com IA",
    href: "/dreams",
    external: false,
    icon: Rocket,
    gradient: "from-[#1a2542]/90 via-[#1a2542]/75 to-[#4B5C8A]/85",
    bgImage: "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=1200&q=75&auto=format&fit=crop",
    bgPos: "center",
    cta: "Começar",
  },
  {
    key: "soulmatch",
    title: "Encontra a tua Alma Gémea",
    href: "https://soulmatch.pt",
    external: true,
    icon: Heart,
    gradient: "from-[#7A1F3D]/85 via-[#7A1F3D]/70 to-[#B54862]/85",
    bgImage: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&q=75&auto=format&fit=crop",
    bgPos: "center 30%",
    cta: "Descobrir",
  },
  {
    key: "felicidade",
    title: "Cria mais Felicidade na tua Empresa",
    href: "https://felicidadeglobal.com",
    external: true,
    icon: Smile,
    gradient: "from-[#C2831A]/85 via-[#C2831A]/70 to-[#E0A93E]/85",
    bgImage: "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=1200&q=75&auto=format&fit=crop",
    bgPos: "center 40%",
    cta: "Saber mais",
  },
];

function HighlightCard({ h }) {
  const inner = (
    <>
      {/* Imagem de fundo com posição controlada */}
      <div
        className="absolute inset-0 bg-cover transition-transform duration-500 group-hover:scale-105"
        style={{ backgroundImage: `url(${h.bgImage})`, backgroundPosition: h.bgPos || "center" }}
        aria-hidden="true"
      />
      {/* Overlay gradient para legibilidade */}
      <div className={`absolute inset-0 bg-gradient-to-br ${h.gradient}`} aria-hidden="true" />
      {/* Conteúdo */}
      <div className="relative z-10">
        <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm grid place-items-center mb-4 group-hover:scale-110 transition-transform ring-1 ring-white/25">
          <h.icon size={20} strokeWidth={1.8} />
        </div>
        <div
          className="text-[15px] leading-snug mb-3 min-h-[42px] drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
        >
          {h.title}
        </div>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/85 group-hover:text-white">
          {h.cta}
          <ArrowRight size={13} className="-translate-x-1 group-hover:translate-x-0 transition-transform" />
        </div>
      </div>
    </>
  );
  const cls = "group relative overflow-hidden rounded-[20px] p-5 text-white hover:shadow-[0_10px_30px_rgba(15,24,41,0.25)] hover:-translate-y-0.5 transition-all block min-h-[180px]";
  return h.external ? (
    <a href={h.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`my-highlight-${h.key}`}>
      {inner}
    </a>
  ) : (
    <Link to={h.href} className={cls} data-testid={`my-highlight-${h.key}`}>
      {inner}
    </Link>
  );
}

export default function MyHome() {
  const { user } = useMyAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [consultor, setConsultor] = useState(null);
  const [formador, setFormador] = useState(null);
  const [trainerOpen, setTrainerOpen] = useState(true);
  const [showTourPrompt, setShowTourPrompt] = useState(false);

  useEffect(() => {
    myApi.dashboard().then(setData).catch(() => setData({}));
    myApi.orders(undefined, 1, 5)
      .then((r) => setOrders((r.items || []).slice(0, 5)))
      .catch(() => setOrders([]));
    myApi.consultor().then(setConsultor).catch(() => setConsultor(null));
    myApi.formadorInfo().then(setFormador).catch(() => setFormador(null));
    // Convite ao tour "Como Funciona" desativado.
  }, []);

  const dismissTour = () => {
    setShowTourPrompt(false);
    myApi.tourMarkSeen().catch(() => {});
  };

  return (
    <div data-testid="my-home-page" className="space-y-8">
      {/* Convite ao Tour "Como Funciona" (só na 1ª vez) */}
      {showTourPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="tour-prompt-modal">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="relative h-40 bg-gradient-to-br from-[#0F1829] via-[#1a2542] to-[#978D78] flex items-center justify-center">
              <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,.25) 0%, transparent 55%)" }} />
              <div className="relative w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                <PlayCircle size={40} className="text-white" />
              </div>
              <button onClick={dismissTour} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white" data-testid="tour-prompt-close" aria-label="Fechar">
                <XIcon size={16} />
              </button>
            </div>
            <div className="p-6 text-center">
              <h3 className="text-lg font-bold text-[#0F1829]">Bem-vindo ao teu portal! 👋</h3>
              <p className="mt-2 text-sm text-slate-600">
                Faz uma pequena volta ao mundo connosco e descobre, em 2 minutos,
                tudo o que podes fazer no teu portal.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <Link to="/my/como-funciona" onClick={() => setShowTourPrompt(false)}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F1829] text-white font-semibold px-4 py-3 rounded-xl hover:bg-[#1a2542] transition-colors"
                      data-testid="tour-prompt-start">
                  <PlayCircle size={18} /> Ver agora
                </Link>
                <button onClick={dismissTour}
                        className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium"
                        data-testid="tour-prompt-dismiss">
                  Agora não
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#0F1829] via-[#1a2542] to-[#978D78] p-8 lg:p-10 text-white">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 15%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 15% 85%, rgba(209,207,201,0.3) 0%, transparent 50%)",
          }}
        />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-[10px] uppercase tracking-[0.25em]">
              <Sparkles size={12} /> Portal do Cliente
            </div>
            {formador?.is_formador && (
              <div
                data-testid="my-trainer-badge"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D1CFC9]/25 backdrop-blur-sm border border-white/25 text-[10px] uppercase tracking-[0.25em] text-white"
                title="É Formador da IHTP"
              >
                <GraduationCap size={12} /> Trainer
              </div>
            )}
          </div>
          <h1
            className="text-3xl lg:text-5xl leading-tight mb-3 max-w-3xl"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
          >
            Olá, <span className="italic text-[#D1CFC9]">{(user?.NomeCompleto || "").split(" ")[0] || "Cliente"}</span>.
          </h1>
          <p className="text-white/75 max-w-xl">
            Aqui tens acesso rápido a tudo o que faz parte da tua experiência IHTP: encomendas,
            produtos, subscrições, vales, prémios e ferramentas para seres mais feliz e mais livre.
          </p>

          {data?.situacao_premio > 0 && (
            <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
              <Star size={16} className="text-[#D1CFC9]" />
              <span className="text-sm">
                Faltam <strong>{Number(data.valor_ate_proximo_premio).toFixed(2)}€</strong> para o próximo prémio
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Destaques IHTP */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="my-highlights">
        {HIGHLIGHTS.map((h) => <HighlightCard key={h.key} h={h} />)}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map((c) => (
          <Link
            key={c.key}
            to={c.href}
            data-testid={`my-stat-${c.key}`}
            className="group relative bg-white rounded-[20px] p-5 border border-[#E6E1D6] hover:border-[#978D78] hover:shadow-[0_8px_24px_rgba(15,24,41,0.08)] transition-all"
          >
            <div className="flex items-start justify-between mb-8">
              <div
                className="w-10 h-10 rounded-full grid place-items-center"
                style={{ backgroundColor: `${c.accent}15`, color: c.accent }}
              >
                <c.icon size={18} strokeWidth={1.7} />
              </div>
              <ArrowRight size={16} className="text-[#978D78] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
            </div>
            <div
              className="text-3xl text-[#0F1829] leading-none"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
            >
              {data
                ? (c.isMoney ? fmt(data[c.key]) : (data[c.key] ?? 0))
                : "—"}
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#978D78] mt-2">
              {c.label}
            </div>
          </Link>
        ))}
      </div>

      {/* Consultor Dedicado */}
      {consultor && (
        <div
          data-testid="my-consultor-card"
          className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8 flex items-center gap-6 flex-wrap"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {consultor.HasPhoto ? (
              <img
                src={`${""}/api/profile/photo/${consultor.ID_Cliente}`}
                alt={consultor.NomeCompleto}
                data-testid="my-consultor-photo"
                className="w-16 h-16 rounded-full object-cover border border-[#E6E1D6] shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F1829] to-[#978D78] grid place-items-center text-white text-2xl shrink-0"
                   style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
                {(consultor.NomeCompleto || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-1 flex items-center gap-1.5">
                <User size={11} /> Consultor Dedicado
              </div>
              <div
                className="text-xl text-[#0F1829] leading-tight truncate"
                style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
                data-testid="my-consultor-name"
              >
                {consultor.NomeCompleto}
              </div>
              <div className="text-xs text-[#6B7280] mt-1">O seu ponto de contacto na IHTP</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {consultor.Email && (
              <a
                href={`mailto:${consultor.Email}`}
                data-testid="my-consultor-email"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-[#F5F2EB] text-[#0F1829] hover:bg-[#E6E1D6] transition"
              >
                <Mail size={14} /> {consultor.Email}
              </a>
            )}
            {consultor.NumeroTelemovelDefault && (
              <a
                href={`tel:${consultor.NumeroTelemovelDefault}`}
                data-testid="my-consultor-phone"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-[#F5F2EB] text-[#0F1829] hover:bg-[#E6E1D6] transition"
              >
                <Phone size={14} /> {consultor.NumeroTelemovelDefault}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-xl text-[#0F1829]"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
          >
            Últimas Encomendas
          </h2>
          <Link to="/my/encomendas" className="text-xs uppercase tracking-[0.2em] text-[#978D78] hover:text-[#0F1829]">
            Ver todas →
          </Link>
        </div>
        {orders.length === 0 ? (
          <div className="py-10 text-center text-sm text-[#6B7280]">Sem encomendas recentes.</div>
        ) : (
          <div className="divide-y divide-[#F5F2EB]">
            {orders.map((o) => (
              <Link
                key={o.ID_Encomenda}
                to={`/my/encomendas/${o.ID_Encomenda}`}
                data-testid={`my-order-row-${o.ID_Encomenda}`}
                className="flex items-center justify-between py-3 px-2 -mx-2 rounded-lg hover:bg-[#F5F2EB] transition"
              >
                <div>
                  <div className="text-sm text-[#0F1829]">#{o.ID_Encomenda}</div>
                  <div className="text-xs text-[#978D78]">{(o.DataEncomenda || "").slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#F5F2EB] text-[#4B5563]">
                    {o.EstadoEncomenda || "—"}
                  </span>
                  <span className="text-sm font-medium text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
                    {Number(o.PrecoFinal || 0).toFixed(2)} {o.ID_Moeda || "EUR"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Documentos pessoais do formador — movidos para a página "Área de Formador"
 * (`MyAreaFormador.jsx`). Mantido aqui apenas o placeholder do fim do ficheiro. */

